import React from "react";
import {
  Box,
  Card,
  Chip,
  TextField,
  Typography,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";

export const CellWrap = ({ children, align = "center" }) => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent:
        align === "left"
          ? "flex-start"
          : align === "right"
            ? "flex-end"
            : "center",
      minWidth: 0,
      py: 0.5,
    }}
  >
    {children}
  </Box>
);

export const metalNameColumn = {
  flex: 0.14,
  minWidth: 130,
  field: "metalName",
  headerName: "Metal Name",
  align: "left",
  headerAlign: "left",
  renderCell: ({ row }) => (
    <CellWrap align="left">
      <Typography
        variant="body2"
        fontWeight={600}
        noWrap
        sx={{ color: "#1e293b", width: "100%" }}
      >
        {row?.bullion?.metalName || "—"}
      </Typography>
    </CellWrap>
  ),
};

export const stateColumn = {
  flex: 0.14,
  minWidth: 130,
  field: "state",
  headerName: "State",
  align: "center",
  headerAlign: "center",
  renderCell: ({ row }) => (
    <CellWrap align="center">
      <Chip
        label={row?.state || "—"}
        size="small"
        sx={{ bgcolor: "#f8fafc", fontWeight: 500 }}
      />
    </CellWrap>
  ),
};

export const buildCountryNameMap = (...groups) => {
  const map = new Map();
  const add = (id, name) => {
    if (id && name) map.set(String(id), name);
  };

  groups.flat().forEach((item) => {
    if (!item) return;
    add(
      item.country?._id || item.country,
      item.countryName || item.country?.countryName,
    );
    (item.marginData || []).forEach((margin) => {
      add(
        margin.country?._id || margin.country,
        margin.country?.countryName || margin.countryName,
      );
    });
  });

  return map;
};

export const withCountryName = (rows = [], nameById = new Map()) =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    const countryName =
      row?.countryName ||
      row?.country?.countryName ||
      nameById.get(String(row?.country?._id || row?.country || "")) ||
      "";

    if (countryName === row?.countryName) return row;
    return { ...row, countryName };
  });

export const countryColumn = {
  flex: 0.14,
  minWidth: 140,
  field: "countryName",
  headerName: "Country",
  align: "center",
  headerAlign: "center",
  renderCell: ({ row }) => (
    <CellWrap align="center">
      <Chip
        label={row?.countryName || row?.country?.countryName || "—"}
        size="small"
        sx={{ bgcolor: "#f8fafc", fontWeight: 500 }}
      />
    </CellWrap>
  ),
};

export const karatColumn = (withK = false) => ({
  flex: 0.08,
  minWidth: 80,
  field: "karat",
  headerName: "Karat",
  align: "center",
  headerAlign: "center",
  renderCell: ({ row }) => {
    const karat = row?.bullion?.karat;
    const label =
      karat == null || karat === "" ? "—" : withK ? `${karat}k` : karat;
    return (
      <CellWrap align="center">
        <Chip
          label={label}
          size="small"
          sx={{ bgcolor: "#f8fafc", fontWeight: 500 }}
        />
      </CellWrap>
    );
  },
});

export const purityColumn = {
  flex: 0.08,
  minWidth: 80,
  field: "purity",
  headerName: "Purity",
  align: "center",
  headerAlign: "center",
  renderCell: ({ row }) => (
    <CellWrap align="center">
      <Chip
        label={row?.bullion?.purity ?? "—"}
        size="small"
        sx={{ bgcolor: "#f8fafc", fontWeight: 500 }}
      />
    </CellWrap>
  ),
};

export const rateTypeColumn = {
  flex: 0.1,
  minWidth: 100,
  field: "rateType",
  headerName: "Rate Type",
  align: "center",
  headerAlign: "center",
  renderCell: ({ row }) => {
    const isBuy = String(row?.rateType || "").toLowerCase() === "buy";
    return (
      <CellWrap align="center">
        <Chip
          label={row?.rateType || "—"}
          size="small"
          color={isBuy ? "success" : "default"}
          variant="outlined"
          sx={
            isBuy
              ? {}
              : { color: "#c2410c", borderColor: "#fed7aa", bgcolor: "#fff7ed" }
          }
        />
      </CellWrap>
    );
  },
};

export const ratePerGramColumn = {
  flex: 0.12,
  minWidth: 120,
  field: "ratePerGram",
  headerName: "Rate Per Gram",
  align: "center",
  headerAlign: "center",
  renderCell: ({ row }) => (
    <CellWrap align="center">
      <Typography variant="body2" sx={{ fontWeight: 600, color: "#752ADA" }}>
        {row?.ratePerGram != null && row?.ratePerGram !== ""
          ? `₹${row.ratePerGram}`
          : "—"}
      </Typography>
    </CellWrap>
  ),
};

export const taxColumn = (headerName) => ({
  flex: 0.08,
  minWidth: 80,
  field: "tax",
  headerName,
  align: "center",
  headerAlign: "center",
  renderCell: ({ row }) => (
    <CellWrap align="center">
      <Typography variant="body2" sx={{ fontWeight: 500, color: "#475569" }}>
        {row?.tax ?? "—"}
      </Typography>
    </CellWrap>
  ),
});

export const rateWithGstColumn = (headerName) => ({
  flex: 0.13,
  minWidth: 130,
  field: "rateWithGst",
  headerName,
  align: "center",
  headerAlign: "center",
  renderCell: ({ row }) => (
    <CellWrap align="center">
      <Typography variant="body2" sx={{ fontWeight: 600, color: "#752ADA" }}>
        {row?.rateWithGst != null && row?.rateWithGst !== ""
          ? `₹${row.rateWithGst}`
          : "—"}
      </Typography>
    </CellWrap>
  ),
});

export const updatedByColumn = {
  flex: 0.14,
  minWidth: 140,
  field: "updatedBy",
  headerName: "Price Updated By",
  align: "left",
  headerAlign: "left",
  renderCell: ({ row }) => (
    <CellWrap align="left">
      <Tooltip title={row?.updatedBy || ""}>
        <Typography
          variant="body2"
          noWrap
          sx={{ color: "#64748b", width: "100%" }}
        >
          {row?.updatedBy || "—"}
        </Typography>
      </Tooltip>
    </CellWrap>
  ),
};

export const dateColumn = ({
  field,
  headerName,
  dateFormat,
  fallback = "—",
}) => ({
  flex: 0.14,
  minWidth: 140,
  field,
  headerName,
  align: "center",
  headerAlign: "center",
  renderCell: ({ row }) => {
    const value = row?.[field];
    return (
      <CellWrap align="center">
        <Typography variant="body2" sx={{ fontWeight: 500, color: "#475569" }}>
          {value ? moment(value).format(dateFormat) : fallback}
        </Typography>
      </CellWrap>
    );
  },
});

export const filterMetalRateRows = (rows = [], searchText = "") => {
  const query = String(searchText || "").trim().toLowerCase();
  const list = Array.isArray(rows) ? rows : [];
  if (!query) return list;

  return list.filter((row) => {
    const values = [
      row?.bullion?.metalName,
      row?.state,
      row?.countryName,
      row?.country?.countryName,
      row?.bullion?.karat,
      row?.bullion?.purity,
      row?.rateType,
      row?.ratePerGram,
      row?.tax,
      row?.rateWithGst,
      row?.updatedBy,
      row?.createdAt ? moment(row.createdAt).format("DD-MM-YYYY") : "",
      row?.validTill ? moment(row.validTill).format("DD-MM-YYYY") : "",
    ];
    return values.some((value) =>
      String(value ?? "").toLowerCase().includes(query),
    );
  });
};

export default function MetalRateGrid({
  title,
  rows = [],
  columns = [],
  loading = false,
  search,
  onSearchChange,
  searchPlaceholder = "Search by metal, state, rate type...",
  pageSize = 10,
  onPageSizeChange,
  page = 0,
  onPageChange,
  rowCount,
  paginationMode = "client",
}) {
  const count = rowCount ?? rows.length;
  const rowsWithId = rows.map((row, index) => ({
    ...row,
    _gridId: row?._id || row?.id || `metal-rate-${index}`,
  }));

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: "1px solid #e8eaf0",
        boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          background: "linear-gradient(135deg, #752ADA 0%, #9b5de5 100%)",
          color: "#fff",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {count} record{count !== 1 ? "s" : ""}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {onSearchChange && (
          <TextField
            placeholder={searchPlaceholder}
            size="small"
            fullWidth
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ mb: 2.5, maxWidth: 420 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />
        )}

        <DataGrid
          autoHeight
          rows={rowsWithId}
          getRowId={(row) => row._gridId}
          columns={columns}
          loading={loading}
          pagination
          paginationMode={paginationMode}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            if (model.pageSize !== pageSize) {
              onPageSizeChange?.(model.pageSize);
            }
            if (model.page !== page) {
              onPageChange?.(model.page);
            }
          }}
          pageSizeOptions={[10, 25, 50]}
          rowCount={count}
          rowHeight={64}
          disableRowSelectionOnClick
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              bgcolor: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              minHeight: "48px !important",
              maxHeight: "48px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
              color: "#475569",
            },
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              borderColor: "#f1f5f9",
              py: 0,
            },
            "& .MuiDataGrid-row": {
              maxHeight: "none !important",
            },
            "& .MuiDataGrid-row:hover": {
              bgcolor: "rgba(117,42,218,0.04)",
            },
          }}
        />
      </Box>
    </Card>
  );
}
